import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { DataSource } from "@seriously-rad/seriouslyrad-framework-core/Data/DataSource";
import { PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { $$, IJNativeEvent } from "@seriously-rad/seriouslyrad-framework-core/JNative";
import { PropertyChange } from "@seriously-rad/seriouslyrad-framework-core/PropertyChange";
import { Property } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { ControlRef } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ControlRef";
import { Dialog, DialogPropertyValues, IDialog } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Dialog";
import { PageFooter } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/PageFooter";
import { PropertyEditorComboBox } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/PropertyEditorComboBox";
import { JsxFragment } from "@seriously-rad/seriouslyrad-framework-core/Ui/JsxFragment";
import { NAMESPACE } from "./_Namespace";

export type ThemePickerDialogPropertyValues =
    DialogPropertyValues
    & {
    };

export type ThemePickerDialogProperties = {
    [name in keyof ThemePickerDialogPropertyValues]: Property<ThemePickerDialogPropertyValues[name]>
};

export interface IThemePickerDialog extends IDialog, ThemePickerDialogProperties
{
}

export class ThemePickerDialog extends Dialog implements IThemePickerDialog
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ThemePickerDialog";
    public static override className = "ThemePickerDialog";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ThemePickerDialogPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                okButtonText: "Done",
                title: "Theme Picker",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _propertyEditorBaseCssControlRef!: ControlRef<PropertyEditorComboBox>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ThemePickerDialogPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ThemePickerDialogPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._propertyEditorBaseCssControlRef = new ControlRef();
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this._propertyEditorBaseCssControlRef.current, "this._propertyEditorBaseCssControlRef.current");

        this._propertyEditorBaseCssControlRef.current.on("valueChanged", { self: this }, this.onPropertyEditorBaseCssValueChanged);
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        const
            dataSourceTheme =
                new DataSource({
                    data: [
                        { value: "/content/seriouslyrad.ui.classic.css", description: "Classic" },
                        { value: "/content/seriouslyrad.ui.friendly-future.css", description: "Friendly Future" },
                    ]
                });

        return (
            <div class="rad-control rad-control-container-control rad-control-page">
                <div ref={this._containerWrapperElementRef} class="rad-control-container-wrapper">
                    <div ref={this._containerInnerWrapperElementRef} class="rad-control-container-inner-wrapper">
                        <div ref={this._headerElementRef} class="rad-page-header ui container fluid">
                            <h2 ref={this._titleElementRef} class="ui dividing header">
                                <span ref={this._titleTextElementRef} class="rad-title"></span>
                            </h2>
                        </div>
                        <div ref={this._containerElementRef} class="rad-control-container ui container fluid">
                            <PropertyEditorComboBox ref={this._propertyEditorBaseCssControlRef} editMode={true} listDataSource={dataSourceTheme} propertyDisplayName="Theme" />
                            {children ?? this._createChildrenHtml()}
                        </div>
                    </div>
                </div>
                <PageFooter ref={this._pageFooterControlRef} />
            </div>
        );
    }

    public override openAsync()
    {
        Assert.isNotNullOrUndefined(this._propertyEditorBaseCssControlRef.current, "this._propertyEditorBaseCssControlRef.current");

        this._propertyEditorBaseCssControlRef.current.value.set($$("#linkBaseStylesheet").attr("href"));
        return super.openAsync();
    }

    // #endregion

    // #region Event Handlers

    protected onPropertyEditorBaseCssValueChanged(evt: IJNativeEvent, change: PropertyChange<string>)
    {
        $$("#linkBaseStylesheet").attr("href", change.currentValue);
    }

    // #endregion

    // #endregion
}

